/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import HeroBanner from "../assets/images/cp-hero-default.jpg";
import {
  AdvaPACSPrivacyAndPolicy,
  AppLoader,
  AppLogo,
  AppPurpose,
  TermsOfServiceLink,
  useBrand,
} from "@advapacs/common";
import { useFlag } from "@unleash/proxy-client-react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { IRootState } from "@src/config/reducers";

interface AuthenticationLayoutProps {
  children: React.ReactElement | React.ReactElement[];
  isPersonalForm?: boolean;
  isMultipleBrand?: boolean;
}

const MAX_HEIGHT_TO_SHOW_CENTER = 930;

export const AuthenticationLayout: FC<AuthenticationLayoutProps> = ({
  children,
  isPersonalForm,
  isMultipleBrand = false,
}) => {
  const { pathname } = useLocation();

  const { isMobileOnly } = useSelector(
    (rootState: IRootState) => rootState.responsiveReducer
  );

  const showAppPurposeOrTermsOfService = useMemo(() => {
    let brandData: { brandPath: string } | undefined;
    try {
      const brandDataRaw = localStorage.getItem("brand_portal");
      if (brandDataRaw && brandDataRaw !== "undefined") {
        brandData = JSON.parse(brandDataRaw);
      }
    } catch {
      brandData = undefined;
    }
    return (
      pathname === "/" ||
      pathname === "/Login" ||
      pathname === `/brand/${brandData?.brandPath}`
    ); // google recaptcha return callback to login page.
  }, [pathname]);

  const showPurposeApp = useMemo(() => {
    return showAppPurposeOrTermsOfService;
  }, [showAppPurposeOrTermsOfService]);

  const [shouldAlignHeight, setShouldAlignHeight] = useState(
    showPurposeApp && window.innerHeight > MAX_HEIGHT_TO_SHOW_CENTER
  );

  const resize = () => {
    setShouldAlignHeight(
      showPurposeApp && window.innerHeight > MAX_HEIGHT_TO_SHOW_CENTER
    );
  };

  const { brand } = useBrand();

  useEffect(() => {
    window.addEventListener("resize", resize);
    return () => {
      window.removeEventListener("resize", resize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getHeroBanner = useCallback(() => {
    if (isMultipleBrand && brand?.bannerUrl) return brand?.bannerUrl;

    return HeroBanner;
  }, [brand, isMultipleBrand]);
  return (
    <>
      <div className="d-flex cp-hero-container">
        <AppLoader.ImageLoader
          className="d-mobile-none d-tablet-none"
          url={getHeroBanner()}
          cached={sessionStorage.getItem("hero-loaded") === "true"}
          cacheImage={() => sessionStorage.setItem("hero-loaded", "true")}
          width={"45%"}
          type="cover"
        />

        <div className="flex-1 pos-r">
          <div
            className={`${
              showPurposeApp ? "pt-4 has-purpose" : "pt-5"
            } cp-auth-right ${shouldAlignHeight ? "algin-height" : ""}`}
          >
            <div
              className={`cp-auth-content ${
                isPersonalForm ? "personal-form" : ""
              }`}
            >
              {children}
            </div>
          </div>
          {showPurposeApp && <AppPurpose isMobileOnly={isMobileOnly} />}

          <div className="navbar-text bg-white  cp-authentication-footer">
            <div className="w-100 text-center p-t-xs ">
              <AppLogo.LogoLong />
            </div>
            <p className=" w-100 text-center m-t-xxs text-label m-b-none ">
              <span>
                © {new Date().getFullYear()} AdvaPACS by{" "}
                <a href="https://adva.health" target="_blank">
                  AdvaHealth Solutions
                </a>
              </span>{" "}
              |
              <AdvaPACSPrivacyAndPolicy className="p-lr-xxs" />
              <>
                |
                <TermsOfServiceLink
                  className="p-l-xxs"
                  link={`${window.location.origin}/public/terms-of-service`}
                />
              </>
            </p>
          </div>
        </div>
      </div>
    </>
  );
};
